import React, { useEffect, useRef } from 'react';
import { VISUAL } from './layout';

export interface BackdropProps {
  /** ember glow position in viewport uv (0..1, bottom-left origin) */
  sun: [number, number];
  /** ember glow strength (0 disables) */
  ember: number;
}

// Fixed photographic backdrop behind the whole landing page: a sunset above
// a sea of clouds ("Sunset from JetBlue Plane1" by Kevin Gill, CC BY 4.0,
// via Wikimedia Commons; credited under the coverage map) with a slow drift,
// a warm ember glow, a darkened upper sky and a darkening overlay that grows
// as the hero scrolls away so the glass sections stay readable.
export const BACKDROP_IMAGE = `${process.env.PUBLIC_URL}/assets/backdrop/sea-of-clouds.jpg`;
// /site3 sits on a blurred copy of its own hero photo instead (graded cool and
// dark), so the warehouse fades into itself rather than into a sunset.
const IS_WAREHOUSE = VISUAL === 'warehouse';
const IMAGE = IS_WAREHOUSE ? `${process.env.PUBLIC_URL}/assets/backdrop/changi-ftz-blur.jpg` : BACKDROP_IMAGE;
export const BACKDROP_CREDIT = IS_WAREHOUSE
  ? null
  : {
      text: 'Background photo: Kevin Gill, CC BY 4.0',
      href: 'https://commons.wikimedia.org/wiki/File:Sunset_from_JetBlue_Plane1.jpg',
    };

const Backdrop: React.FC<BackdropProps> = ({ sun, ember }) => {
  const dimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const p = Math.min(1, window.scrollY / Math.max(1, window.innerHeight));
      if (dimRef.current) dimRef.current.style.opacity = String(Math.min(0.66, p * 0.8));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={`lp-backdrop ${IS_WAREHOUSE ? 'lp-backdrop--warehouse' : ''}`} aria-hidden="true">
      <img src={IMAGE} alt="" className="lp-backdrop-img" draggable={false} />
      {IS_WAREHOUSE && <div className="lp-backdrop-cool" />}
      <div
        className="lp-backdrop-ember"
        style={{
          opacity: ember,
          background: `radial-gradient(36% 34% at ${(sun[0] * 100).toFixed(0)}% ${((1 - sun[1]) * 100).toFixed(0)}%, rgba(255, 160, 70, 0.55), rgba(255, 120, 40, 0.18) 45%, transparent 72%)`,
        }}
      />
      <div className="lp-backdrop-tint" />
      <div ref={dimRef} className="lp-backdrop-dim" />
    </div>
  );
};

export default Backdrop;
